import React from 'react';
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Badge,
} from '@tonic/shared/components/ui';
import { DashboardChart } from '@tonic/shared/components/ui';
import { Leaf, Droplet, Wind, TreePine } from 'lucide-react';

// Mock data for environmental metrics
const mockEnvironmentalData = {
  carbonFootprint: [
    { month: 'Jan', saved: 1250, baseline: 1500 },
    { month: 'Feb', saved: 1380, baseline: 1500 },
    { month: 'Mar', saved: 1420, baseline: 1500 },
    { month: 'Apr', saved: 1480, baseline: 1500 },
  ],
  waterConservation: [
    { month: 'Jan', saved: 25000, baseline: 30000 },
    { month: 'Feb', saved: 27500, baseline: 30000 },
    { month: 'Mar', saved: 28400, baseline: 30000 },
    { month: 'Apr', saved: 29200, baseline: 30000 },
  ],
  energyEfficiency: [
    { month: 'Jan', consumption: 85, target: 80 },
    { month: 'Feb', consumption: 82, target: 80 },
    { month: 'Mar', consumption: 79, target: 80 },
    { month: 'Apr', consumption: 77, target: 80 },
  ],
  materialRecovery: [
    { material: 'Wood', rate: 92 },
    { material: 'Metal', rate: 95 },
    { material: 'Plastic', rate: 88 },
    { material: 'Glass', rate: 94 },
    { material: 'Paper', rate: 91 },
  ],
  landfillDiversion: [
    { month: 'Jan', diverted: 88 },
    { month: 'Feb', diverted: 90 },
    { month: 'Mar', diverted: 92 },
    { month: 'Apr', diverted: 93 },
  ],
  environmentalCertifications: [
    { name: 'ISO 14001', status: 'Active', expiry: '2025-12' },
    { name: 'Green Business', status: 'Active', expiry: '2024-08' },
    { name: 'Zero Waste', status: 'Pending', expiry: '2024-06' },
  ],
};

const timeRanges = [
  { value: '7d', label: 'Last 7 Days' },
  { value: '30d', label: 'Last 30 Days' },
  { value: '90d', label: 'Last 90 Days' },
  { value: '12m', label: 'Last 12 Months' },
];

export function RecycleEnvironmentPage() {
  const [timeRange, setTimeRange] = React.useState('30d');

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">Environmental Impact</h1>
          <p className="text-sm text-muted-foreground">
            Track and analyze environmental metrics and sustainability goals
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Leaf className="w-5 h-5 text-muted-foreground" />
          <Select value={timeRange} onValueChange={setTimeRange}>
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {timeRanges.map((range) => (
                <SelectItem key={range.value} value={range.value}>
                  {range.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Carbon Footprint Reduction</CardTitle>
          </CardHeader>
          <CardContent>
            <DashboardChart
              type="line"
              data={mockEnvironmentalData.carbonFootprint}
              dataKey="saved"
              xAxisKey="month"
              height={300}
              colors={['#10B981']}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Water Conservation</CardTitle>
          </CardHeader>
          <CardContent>
            <DashboardChart
              type="line"
              data={mockEnvironmentalData.waterConservation}
              dataKey="saved"
              xAxisKey="month"
              height={300}
              colors={['#3B82F6']}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Energy Efficiency</CardTitle>
          </CardHeader>
          <CardContent>
            <DashboardChart
              type="line"
              data={mockEnvironmentalData.energyEfficiency}
              dataKey="consumption"
              xAxisKey="month"
              height={300}
              colors={['#F59E0B']}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Material Recovery Rates</CardTitle>
          </CardHeader>
          <CardContent>
            <DashboardChart
              type="bar"
              data={mockEnvironmentalData.materialRecovery}
              dataKey="rate"
              xAxisKey="material"
              height={300}
              colors={['#6366F1']}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Landfill Diversion Rate</CardTitle>
          </CardHeader>
          <CardContent>
            <DashboardChart
              type="line"
              data={mockEnvironmentalData.landfillDiversion}
              dataKey="diverted"
              xAxisKey="month"
              height={300}
              colors={['#10B981']}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Environmental Certifications</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {mockEnvironmentalData.environmentalCertifications.map((cert) => (
                <div key={cert.name} className="flex items-center justify-between p-4 border rounded-lg">
                  <div className="space-y-1">
                    <p className="font-medium">{cert.name}</p>
                    <p className="text-sm text-muted-foreground">Expires: {cert.expiry}</p>
                  </div>
                  <Badge
                    variant={cert.status === 'Active' ? 'success' : 'warning'}
                    className="capitalize"
                  >
                    {cert.status}
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
